/**
 * <TutorMediaPreviewModal> — une photo ou un PDF, avant d'en tirer une activité (template).
 *
 * L'ancienne `MediaPreviewModal.js`, propre au tuteur dans la conversation avec un élève. Deux
 * moments, deux titres, et jamais plus de deux actions :
 *  - « Aperçu » : le tuteur touche une pièce que l'élève a envoyée et dont aucune activité n'est
 *    née. Une seule action, « Créer une activité ». Plusieurs photos dans le fil : deux flèches
 *    pour passer de l'une à l'autre, et entre elles son rang (« 2 / 3 », que le legacy posait dans
 *    l'en-tête) ;
 *  - « Nouveau fichier » : le tuteur vient de choisir une photo ou un PDF dans le composeur.
 *    « Créer une activité », ou « Envoyer dans le chat ».
 * Un PDF se montre par son glyphe et son nom, comme le legacy ; une photo, entière dans sa boîte
 * (`<FittedImage>`) — le zoom du legacy (+ / −) revient à la visionneuse, qu'un appui ouvre.
 *
 * « Créer une activité » ferme l'aperçu et ouvre « Générer une activité » sur ce fichier, à l'étape
 * du fichier lu par Sam : l'écran le fait.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { FittedImage } from "../../atoms/FittedImage"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { Modal } from "../../molecules/Modal"
import { type UITutorMediaPreview } from "../../../ports/primary/ui/types/tutorEleves"

const TEXTS = {
    existing: "Aperçu",
    fresh: "Nouveau fichier",
    create: "Créer une activité",
    send: "Envoyer dans le chat",
    pdf: "Document PDF",
}

export type TutorMediaPreviewModalProps = {
    visible: boolean
    onClose: () => void
    media: UITutorMediaPreview
    /** Le fichier vient d'être choisi (« Nouveau fichier ») : il peut aussi partir dans le chat. */
    fresh?: boolean
    onCreateActivity: () => void
    /** « Envoyer dans le chat » — seulement pour un fichier neuf. */
    onSendToChat?: () => void
    /** L'envoi dans le chat est parti. */
    sending?: boolean
    /** Plusieurs photos dans le fil : le rang de celle-ci (0) et leur nombre. */
    position?: { index: number; count: number } | null
    onPrevious?: () => void
    onNext?: () => void
    /** Un appui sur la photo : la visionneuse plein écran. */
    onOpenViewer?: () => void
    testID?: string
}

export function TutorMediaPreviewModal(props: TutorMediaPreviewModalProps) {
    const {
        visible,
        onClose,
        media,
        fresh = false,
        onCreateActivity,
        onSendToChat,
        sending = false,
        position = null,
        onPrevious,
        onNext,
        onOpenViewer,
        testID,
    } = props
    const canNavigate = position != null && position.count > 1

    const footer = (
        <Stack gap="2">
            <Button label={TEXTS.create} iconName="magic" fullWidth onPress={onCreateActivity} />
            {fresh && onSendToChat != null && (
                <Button
                    label={TEXTS.send}
                    variant="outline"
                    iconName="telegram-plane"
                    fullWidth
                    onPress={onSendToChat}
                    loading={sending}
                />
            )}
        </Stack>
    )

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={fresh ? TEXTS.fresh : TEXTS.existing}
            footer={footer}
            testID={testID}
        >
            {media.kind === "document" ? (
                <Card elevation="none" background="surface-subtle" p="6">
                    <Stack gap="3" align="center">
                        <Icon name="file-pdf" size={24} color="danger" />
                        <Text size="text-md" color="text-muted" center>
                            {media.name ?? TEXTS.pdf}
                        </Text>
                    </Stack>
                </Card>
            ) : (
                <Stack gap="2">
                    <Card elevation="none" background="surface-subtle" p="0" style={styles.image}>
                        <FittedImage uri={media.uri} onPress={onOpenViewer} accessibilityLabel="Photo envoyée" />
                    </Card>
                    {canNavigate && (
                        <Row justify="center" align="center" gap="4">
                            <IconButton
                                iconName="chevron-left"
                                appearance="transparent"
                                iconColor="primary"
                                size={40}
                                disabled={position!.index === 0}
                                onPress={onPrevious}
                                accessibilityLabel="Photo précédente"
                            />
                            <Text size="text-md" color="text-muted">
                                {`${position!.index + 1} / ${position!.count}`}
                            </Text>
                            <IconButton
                                iconName="chevron-right"
                                appearance="transparent"
                                iconColor="primary"
                                size={40}
                                disabled={position!.index >= position!.count - 1}
                                onPress={onNext}
                                accessibilityLabel="Photo suivante"
                            />
                        </Row>
                    )}
                </Stack>
            )}
        </Modal>
    )
}

const styles = StyleSheet.create(() => ({
    image: { height: 360, overflow: "hidden" },
}))
